import axios from 'axios';
import { getApiUrl } from '../config/api';

const api = axios.create({
  baseURL: getApiUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is invalid or expired, redirect to login
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface User {
  id?: number;
  email: string;
  fname: string;
  lname: string;
  dob: string;
  phone: string;
  nif: string;
  address: string;
  password?: string;
  status?: string;
  is_admin?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Item {
  id?: number;
  code: string;
  name: string;
  display_name?: string;
  description: string;
  cost: number;
  price: number;
  is_alcohol?: boolean;
  on_hand?: number;
  tags?: string;
  barcode?: string;
  created_at?: string;
  updated_at?: string;
  has_images?: boolean; // For UI display
}

export interface ItemImage {
  id: number;
  image_type: 'front' | 'back' | 'label';
  image_data: string;
  created_at: string;
}

// Inventory removed; on_hand is now on ITEM

export interface Purchase {
  id?: number;
  user_email: string;
  item_id: number;
  price_asked: number;
  price_paid: number;
  purchased_on: string;
  settled_on?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Payment {
  id?: number;
  user: string;
  amount: number;
  created_at?: string;
  updated_at?: string;
  fname?: string;
  lname?: string;
}

export interface Event {
  id?: number;
  name: string;
  starting_at: string;
  ending_at: string;
  description: string;
  event_type: 'music' | 'poetry' | 'lecture' | 'dinner' | 'party' | 'snooker' | 'other';
  event_other?: string;
  organizer: string;
  cost?: number;
  price: number;
  created_at?: string;
  updated_at?: string;
  organizer_user?: User;
}

export interface DashboardStats {
  totalUsers: number;
  totalItems: number;
  totalInventory: number;
  totalPurchases: number;
}

// Suggestions
export interface Suggestion {
  id?: number;
  creator: string;
  suggestion: string;
  response?: string;
  responder?: string;
  responded_at?: string;
  created_at?: string;
  updated_at?: string;
  creator_fname?: string;
  creator_lname?: string;
  responder_fname?: string;
  responder_lname?: string;
}

// Health check
export const checkHealth = () => api.get('/health');

// Users API
export const getUsers = (page = 1, limit = 10) => 
  api.get(`/api/users?page=${page}&limit=${limit}`);

export const createUser = (user: User) => 
  api.post('/api/users', user);

export const updateUser = (email: string, user: Partial<User>) => 
  api.put(`/api/users/${email}`, user);

export const deleteUser = (email: string) => 
  api.delete(`/api/users/${email}`);

// Items API
export const getItems = (page = 1, limit = 10, search?: string) => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('limit', limit.toString());
  if (search && search.trim().length > 0) params.append('search', search.trim());
  return api.get(`/api/items?${params.toString()}`);
};

export const createItem = (item: Item) => 
  api.post('/api/items', item);

export const updateItem = (id: number, item: Partial<Item>) => 
  api.put(`/api/items/${id}`, item);
// Item Properties
export interface ItemProperty { id?: number; item_id: number; name: string; value: string; created_at?: string; updated_at?: string; }
export const listItemProperties = (itemId?: number) => api.get(`/api/item-properties${itemId ? `?item_id=${itemId}` : ''}`);
export const createItemProperty = (p: ItemProperty) => api.post('/api/item-properties', p);
export const updateItemProperty = (id: number, p: Partial<ItemProperty>) => api.put(`/api/item-properties/${id}`, p);
export const deleteItemProperty = (id: number) => api.delete(`/api/item-properties/${id}`);

// Tasting Notes
export interface TastingNote { 
  id?: number; 
  item_id: number; 
  user?: string; 
  notes: string; 
  source?: string;
  fname?: string;
  lname?: string;
  email?: string;
  created_at?: string; 
  updated_at?: string; 
}
export const listTastingNotes = (itemId?: number) => api.get(`/api/tasting-notes${itemId ? `?item_id=${itemId}` : ''}`);
export const createTastingNote = (n: Partial<TastingNote>) => api.post('/api/tasting-notes', n);
export const updateTastingNote = (id: number, n: Partial<TastingNote>) => api.put(`/api/tasting-notes/${id}`, n);
export const deleteTastingNote = (id: number) => api.delete(`/api/tasting-notes/${id}`);

export const deleteItem = (id: number) => 
  api.delete(`/api/items/${id}`);

// Items: image processing
export const makeItemLabel = (id: number, mode?: 'detect' | 'generate') => 
  api.post(`/api/items/${id}/make-label${mode ? `?mode=${mode}` : ''}`);

// Inventory API
// Inventory endpoints removed

// Purchases API
export const getPurchases = (page = 1, limit = 10, userEmail?: string) => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('limit', limit.toString());
  if (userEmail) {
    params.append('userEmail', userEmail);
  }
  return api.get(`/api/purchases?${params.toString()}`);
};

export const createPurchase = (purchase: Purchase) => 
  api.post('/api/purchases', purchase);

export const updatePurchase = (id: number, purchase: Partial<Purchase>) => 
  api.put(`/api/purchases/${id}`, purchase);

export const deletePurchase = (id: number) => 
  api.delete(`/api/purchases/${id}`);

// Events API
export const getEvents = (page = 1, limit = 10) =>
  api.get(`/api/events?page=${page}&limit=${limit}`);

export const createEvent = (event: Event) =>
  api.post('/api/events', event);

export const updateEvent = (id: number, event: Partial<Event>) =>
  api.put(`/api/events/${id}`, event);

export const deleteEvent = (id: number) =>
  api.delete(`/api/events/${id}`);

// Event Images
export interface EventImage {
  id: number;
  event_id: number;
  name: string;
  image_data: string;
  created_at?: string;
  updated_at?: string;
}

export const listEventImages = (eventId: number) =>
  api.get<{ success: boolean; data: EventImage[] }>(`/api/events/${eventId}/images`);

export const createEventImage = (eventId: number, payload: { name: string; image_data: string }) =>
  api.post<{ success: boolean; data: EventImage }>(`/api/events/${eventId}/images`, payload);

export const updateEventImage = (eventId: number, imageId: number, payload: Partial<{ name: string; image_data: string }>) =>
  api.put<{ success: boolean; data: EventImage }>(`/api/events/${eventId}/images/${imageId}`, payload);

export const deleteEventImage = (eventId: number, imageId: number) =>
  api.delete(`/api/events/${eventId}/images/${imageId}`);

export const generateEventFlyer = (eventId: number) =>
  api.post<{ success: boolean; data: { stored: string[]; images: EventImage[] } }>(
    `/api/events/${eventId}/generate-flyer`,
    {},
    { timeout: 600000 }
  );

// Payments API
export const getPayments = (page = 1, limit = 10) => 
  api.get(`/api/payments?page=${page}&limit=${limit}`);

export const createPayment = (payment: Payment) => 
  api.post('/api/payments', payment);

export const updatePayment = (id: number, payment: Partial<Payment>) => 
  api.put(`/api/payments/${id}`, payment);

export const deletePayment = (id: number) => 
  api.delete(`/api/payments/${id}`);

// Receipt Processing API
export interface ReceiptItem {
  name: string;
  cost: number;
}

export interface ReceiptProcessingResult {
  items: ReceiptItem[];
  csv: string;
  itemCount: number;
  processingResults?: {
    created: number;
    updated: number;
    inventoryUpdated: number;
    errors: string[];
  };
}

export const processReceipt = (imageFile: File) => {
  const formData = new FormData();
  formData.append('image', imageFile);
  
  return api.post('/api/receipts/process', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const getReceiptHistory = (page = 1, limit = 10) => 
  api.get(`/api/receipts/history?page=${page}&limit=${limit}`);

// Dashboard stats - optimized to reduce API calls
export const getDashboardStats = async (): Promise<DashboardStats> => {
  try {
    // Use cached stats or fetch only once per session
    const [usersRes, itemsRes, purchasesRes] = await Promise.all([
      getUsers(1, 1),
      getItems(1, 1),
      getPurchases(1, 1),
    ]);

    return {
      totalUsers: usersRes.data.pagination?.total || 0,
      totalItems: itemsRes.data.pagination?.total || 0,
      totalInventory: 0,
      totalPurchases: purchasesRes.data.pagination?.total || 0,
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    return {
      totalUsers: 0,
      totalItems: 0,
      totalInventory: 0,
      totalPurchases: 0,
    };
  }
};

// Suggestions API (admin)
export const getSuggestions = () => api.get('/api/suggestions');
export const getSuggestion = (id: number) => api.get(`/api/suggestions/${id}`);
export const createSuggestion = (suggestion: string) => api.post('/api/suggestions', { suggestion });
export const updateSuggestion = (id: number, data: Partial<Suggestion>) => api.put(`/api/suggestions/${id}`, data);
export const deleteSuggestion = (id: number) => api.delete(`/api/suggestions/${id}`);

// Wine Learning API
export interface WineAnalysis {
  name: string;
  vintage: string | null;
  country: string;
  region: string | null;
  grapes: string[];
  alcohol_content: string;
  average_price: string;
  tasting_notes: string;
  average_rating: string;
  winery: string;
  description: string;
}

export interface WineLearningResponse {
  success: boolean;
  message: string;
  data?: {
    item: Item;
    analysis: WineAnalysis;
  };
  error?: string;
}

export interface WineImage {
  id: number;
  image_type: 'front' | 'back';
  image_data: string;
  created_at: string;
}

export const learnWineFromImages = (frontImage: string, backImage?: string) => 
  api.post<WineLearningResponse>('/api/wine-learning/learn-wine', {
    frontImage,
    backImage,
  });

export const getWineImages = (itemId: number) => 
  api.get<{ success: boolean; data: WineImage[] }>(`/api/wine-learning/wine-images/${itemId}`);

// Use the same WineImage interface for ItemImage
export const getItemImages = (itemId: number) => 
  api.get<{ success: boolean; data: ItemImage[] }>(`/api/wine-learning/wine-images/${itemId}`);

export default api;
